import { forwardRef, memo } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { FileIcon, FileWarnIcon } from '@page/icons';

import { FileSource, VaFileTypeOrigin } from '@page/core/types/files';
import { Tooltip } from '../../designSystem';

const fileLinkStyles = tv({
  base: 'flex items-center font-body-sm cursor-pointer select-none gap-0.5 py-1 rounded-full w-fit max-w-full hover:underline transition-all',
  variants: {
    variant: {
      default: '',
      outline: 'px-2 pr-2.5 border',
    },
    sourceType: {
      [FileSource.Cfile]: 'text-text-tertiary',
      [FileSource.External]: 'text-text-warning dark:text-orange-7',
    },
    contentDetected: {
      true: '[&_svg]:text-text-tertiary',
      false: '',
    },
    active: {
      true: 'text-icon-link bg-bg-inter-tertiary-active',
      false: '',
    },
  },
  compoundVariants: [
    { variant: 'default', active: true, class: 'px-2 pr-2.5' },
    { variant: 'outline', active: false, class: 'border-border-primary' },
    { variant: 'outline', active: true, class: 'border-bg-inter-tertiary-active' },
    { contentDetected: true, active: true, class: '[&_svg]:text-icon-link' },
  ],
  defaultVariants: {
    variant: 'default',
    active: false,
  },
});

export type FileLinkProps = React.ComponentPropsWithoutRef<'button'> &
  React.ComponentPropsWithoutRef<'a'> &
  VariantProps<typeof fileLinkStyles> & {
    children?: React.ReactNode;
    className?: string;
    as?: 'button' | 'a';
    vaFileTypeOrigin?: VaFileTypeOrigin | null;
    contentDetectedTooltip?: string;
    externalTooltip?: string;
  };

/**
 * A link icon that indicates a file link. It's typically a button that can be clicked to open the file preview.
 */
const FileLinkBase = (
  {
    children,
    active,
    variant,
    className,
    sourceType = FileSource.Cfile,
    vaFileTypeOrigin,
    contentDetectedTooltip,
    externalTooltip,
    as: Comp = 'button',
    ...rest
  }: FileLinkProps,
  ref?: React.ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
) => {
  const contentDetected = vaFileTypeOrigin === VaFileTypeOrigin.FiletypeDetectionV2;
  const Icon = sourceType !== FileSource.Cfile || contentDetected ? FileWarnIcon : FileIcon;
  const tooltipLabel = contentDetected
    ? contentDetectedTooltip
    : sourceType === FileSource.Cfile
      ? undefined
      : externalTooltip;

  return (
    <Comp
      ref={(node: HTMLButtonElement | HTMLAnchorElement | null) => {
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      className={fileLinkStyles({ variant, active: !!active, sourceType, contentDetected, className })}
      {...rest}
    >
      <Tooltip asChild label={tooltipLabel} delayDuration={300} withinPortal className="z-201">
        <Icon className="size-4 fill-current shrink-0" />
      </Tooltip>

      <span className="truncate">{children}</span>
    </Comp>
  );
};

export const FileLink = memo(forwardRef(FileLinkBase));
